import React from "react";
import UseCaseTemplate from "./UseCaseTemplate";
import realHero from "./assets/real-estate/realstate.png";
import real1 from "./assets/real-estate/realstate1.jpg";
import real2 from "./assets/real-estate/realstate02.jpg";
import real3 from "./assets/real-estate/realstate3.jpg";
import real4 from "./assets/real-estate/realstate4.jpg";
import real5 from "./assets/real-estate/realstate5.jpg";
import realVideo from "./assets/real-estate/realstate.mp4";

const experiences = [
  {
    id: "living-property-listing",
    number: "01",
    category: "Property marketing",
    title: "The Living Property Listing",
    lead: "Keep a walkthrough visually current for weeks or months without resetting the listing URL or its accumulated reach.",
    problem: "When a property remains on the market, the original video becomes obsolete as weather, interior layout, paint or staging changes. Full reshoots are expensive and can reset view counts.",
    layout: "Storyboard",
    layoutDetail: "Strict chronological sequence, 5 to 6 slots",
    workflowTitle: "Perpetually updated walkthrough",
    workflow: [
      "Exterior facade.",
      "Living room / Lounge.",
      "Modern kitchen.",
      "Primary bedroom suite.",
      "Terrace / Garden.",
      "The agent refilms only the slot that changed; the HLS stream updates across the agency site and partner portals without changing the listing URL or resetting SEO rankings.",
    ],
    outcome1Label: "B2B impact",
    outcome1: "Up to an 80% reduction in agency audiovisual production costs while inventory stays visually fresh until closing.",
    outcome2Label: "Operational value",
    outcome2: "Agents can capture each room in 8 to 12 seconds on a smartphone and update only the relevant slot when conditions improve.",
    image: real1,
    video: realVideo,
  },
  {
    id: "hyperlocal-ecosystem",
    number: "02",
    category: "Neighborhood pulse",
    title: "The Hyperlocal Ecosystem",
    lead: "Extend the property story beyond the front door and make the surrounding lifestyle visible.",
    problem: "Homebuyers do not just purchase square footage—they buy a lifestyle and a neighborhood. Traditional walkthroughs usually stop at the property boundary.",
    layout: "Relay / Gallery",
    layoutDetail: "Kinetic hand-off through nearby places and local voices",
    workflowTitle: "Neighborhood relay",
    workflow: [
      "Start inside the living room.",
      "Pass the narrative baton to the three-minute walking path to the elementary school.",
      "Continue to the corner café terrace or weekly open-air market.",
      "Local business owners or neighborhood agents submit verified geolocated micro-slots.",
      "Relational AI maintains spatial continuity while Generative RAG AI harmonizes lighting and color across devices.",
    ],
    outcome1Label: "B2B impact",
    outcome1: "A clear competitive edge over discount and low-touch brokerages by proving hyperlocal advisory expertise.",
    outcome2Label: "Buyer value",
    outcome2: "Prospects can evaluate the lifestyle around the home, not only the rooms inside it.",
    image: real2,
  },
  {
    id: "before-after-staging",
    number: "03",
    category: "Renovation potential",
    title: "Space Potential & Before/After Home Staging",
    lead: "Help buyers understand what a dated or renovation-ready room can become.",
    problem: "Older, cluttered or renovation-ready properties often fail to attract buyers who struggle to visualize spatial potential from the raw space.",
    layout: "Versus",
    layoutDetail: "Synchronized 50/50 split-screen",
    workflowTitle: "Room-by-room transformation",
    workflow: [
      "Left side: raw footage of the dated living room or kitchen in its current state.",
      "Right side: 3D virtual home staging projection or finished footage of the renovated room.",
      "Prospective buyers use an interactive split slider or synchronized stream to compare the two states immediately.",
    ],
    outcome1Label: "B2B impact",
    outcome1: "Accelerates purchase offers on fixer-uppers and limits steep, unfounded price-drop negotiations.",
    outcome2Label: "Buyer value",
    outcome2: "Makes transformation potential explicit without requiring the buyer to mentally reconstruct the space.",
    image: real3,
  },
  {
    id: "construction-milestones",
    number: "04",
    category: "Off-plan / VEFA",
    title: "Construction Milestones & Off-Plan Sales",
    lead: "Give buyers one permanent visual thread that updates as the development progresses.",
    problem: "In off-plan developments or full rehabilitations, buyers experience anxiety when visual updates are scarce, driving repetitive support calls and cancellation risk.",
    layout: "Storyboard",
    layoutDetail: "Cadenced interval updates",
    workflowTitle: "Monthly construction progression",
    workflow: [
      "Structural shell and framing.",
      "Model unit interior finish.",
      "Exterior facade and masonry.",
      "Landscaped green spaces and access.",
      "Every month the site manager or sales representative captures 10 seconds per slot; the Weave recompiles on the first of each month.",
    ],
    outcome1Label: "B2B impact",
    outcome1: "Substantial reduction in buyer reassurance calls and a persuasive sales asset for selling remaining lots in late-stage developments.",
    outcome2Label: "Buyer value",
    outcome2: "One permanent link shows progress over time without the clutter of multiple fragmented video files.",
    image: real4,
  },
  {
    id: "exclusive-mandates",
    number: "05",
    category: "Listing acquisition",
    title: "Winning Exclusive Listing Mandates",
    lead: "Use differentiated living-media capability as part of the agent’s valuation and listing pitch.",
    problem: "During appraisal and listing meetings, convincing homeowners to sign an exclusive full-commission mandate is difficult when the marketing promise looks like every legacy alternative.",
    layout: "Symphony",
    layoutDetail: "Fast-paced, dynamic branded showcase",
    workflowTitle: "Live listing-presentation pitch",
    workflow: [
      "Show the platform live on a tablet during the appraisal.",
      "Explain that the home will not be trapped in a static video that loses relevance after two weeks.",
      "Demonstrate weekly updates for optimal sunlight, local market insights and neighborhood life.",
      "Show AI-verified synchronization across listing portals without breaking links.",
    ],
    outcome1Label: "B2B impact",
    outcome1: "Significantly higher conversion from home appraisals to signed exclusive mandates through differentiated marketing technology.",
    outcome2Label: "Seller value",
    outcome2: "A homeowner sees a concrete plan for keeping the property marketing current rather than a one-off launch asset.",
    image: real5,
  },
];

export default function RealEstatePage() {
  return (
    <UseCaseTemplate
      pageTitle={"Real Estate"}
      currentPath={"/use-cases/real-estate"}
      eyebrow={"WEAVEVO USE CASE · REAL ESTATE"}
      heroTitle={"Turn property listings into living media."}
      heroText={"Keep listings fresh, extend the story into the neighborhood, reveal renovation potential and show construction progress without replacing the permanent property link."}
      heroAlt={"Real estate living listing concept powered by Weavevo"}
      finalEyebrow={"BUILD THE NEXT PROPERTY WEAVE"}
      finalTitle={"A property listing should evolve until the deal closes."}
      finalText={"Refresh only what changed, connect the home to its neighborhood and turn a static listing into a persistent, differentiated sales asset."}
      heroImage={realHero}
      experiences={experiences}
    />
  );
}
